import ControllerBase from 'global/js/ControllerBase';
import UIkit from 'uikit';

global ? (global['UIkit'] = UIkit) : undefined;

interface ComponentRefs {
	slider: 'slider';
	/** The table */
	table: 'table';
	/** The DIV which contains the show all button */
	showAllWrapper: 'showAllWrapper';
}

// UIkit doesn't seem to expose it's types so well, for the slider events, so create one
type T_UKSlider = { index: number } & UIkit.UIkitSliderOptions;

export default class ControllerProfessionalProductTableMobile extends ControllerBase<ComponentRefs> {
	private slider: HTMLDivElement;
	private table: HTMLDivElement;
	private tableRows: NodeListOf<HTMLDivElement>;
	private currentTableDataIndex = -1;
	private showAllButton: HTMLAnchorElement | null;
	private hideAllButton: HTMLAnchorElement | null;

	constructor(element: HTMLElement) {
		super(element);

		this.init();
	}

	private init(): void {
		this.table = this.$references.table as HTMLDivElement;
		// find rows, if there are any
		this.tableRows = this.table && this.table.querySelectorAll('div.col');

		this.slider = this.$references.slider as HTMLDivElement;
		// listen for slide change events
		this.slider && this.slider.addEventListener('itemshown', (e) => this.observeUKSlideChange(e as any));

		if (this.$references.showAllWrapper) {
			// get a ref to the button
			this.showAllButton = this.$references.showAllWrapper.querySelector<HTMLAnchorElement>('.a-button.m-professional-product-table-mobile__show');
			this.showAllButton && this.showAllButton.addEventListener('click', (e) => this.handleShowAll(e));

			this.hideAllButton = this.$references.showAllWrapper.querySelector<HTMLAnchorElement>('.a-button.m-professional-product-table-mobile__hide');
			this.hideAllButton && this.hideAllButton.addEventListener('click', (e) => this.handleShowAll(e));
		}
	}

	/**
	 * Observe UIkit slider events and update the table column being shown
	 * @param  {CustomEvent<T_UKSlider[]>} event
	 * @returns void
	 */
	private observeUKSlideChange(event: CustomEvent<T_UKSlider[]>): void {
		if (event.detail.length > 0) {
			const slider = event.detail[0];

			if (slider.index > -1 && this.currentTableDataIndex !== slider.index) {
				this.currentTableDataIndex = slider.index;
				// show new column
				this.showColumn();
			}
		}
	}

	/**
	 * Show a specific table column by index and hide all others
	 * @param  {number} index?
	 * @returns void
	 */
	private showColumn(index?: number): void {
		const column = index !== undefined ? index : this.currentTableDataIndex;

		if (!this.tableRows || this.tableRows.length === 0) return;

		// for each row, show the child cell, whose index corresponds to the uk-slider slide index
		// if the slider is at position 0, the cell to be shown is actually index 1 because the first column is fixed
		this.tableRows.forEach((row) => {
			const children = Array.from(row.children as HTMLCollectionOf<HTMLDivElement>);

			// hide all children except the first column. The first column is: div.cell.cell-body.cell--first
			children.forEach((c, childIndex) => {
				// if the cell is not the first and not already hidden, hide it
				if (false === c.classList.contains('cell--first') && false === c.classList.contains('cell--hidden')) {
					c.classList.add('cell--hidden');
				}
			});

			// make new column not hidden
			if (children.length >= this.currentTableDataIndex + 1) {
				children[this.currentTableDataIndex + 1].classList.remove('cell--hidden');
			}
		});
	}

	/**
	 * When a user clicks on the show all button, unhide all the hidden table rows
	 * @param  {MouseEvent} event
	 * @returns void
	 */
	private handleShowAll(event: MouseEvent): void {
		event.preventDefault();

		const hiddenRows = this.table.querySelectorAll<HTMLDivElement>('div.col--hidden');

		hiddenRows.forEach((c) => c?.classList.toggle('col--shown'));

		// remove the bottom white gradient, by removing the class
		this.table.classList.toggle('truncated-y');

		//Toggle hide/show buttons when
		this.showAllButton?.classList.toggle('uk-hidden');
		this.hideAllButton?.classList.toggle('uk-hidden');
	}
}
